import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Stack,
  Pagination,
  Paper,
  InputAdornment,
  Button,
  Alert,
  Skeleton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  PublicCompanyGrid,
  CompanyGridItem,
} from '../LoginPage/PublicCompanyGrid';

const toMultiSelectValues = (value: string | string[]): string[] =>
  typeof value === 'string' ? (value ? value.split(',') : []) : value;

const PAGE_SIZE = 9;

// 都道府県リスト（業種・職種は実データから動的生成）
const PREFECTURES = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
  '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
  '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
  '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
];

interface CompanyListViewProps {
  /** 表示対象の企業（管理者プレビューでは isPublished を含みうる） */
  companies: CompanyGridItem[];
  loading: boolean;
  error: string | null;
  /** カードクリック時の処理（企業詳細への遷移など） */
  onDetailsClick: (companyId: number) => void;
  /** ヘッダーのタイトル */
  title?: string;
  /** ヘッダーのサブタイトル */
  subtitle?: string;
  /** ヘッダー右側に差し込むスロット（トグル等） */
  headerRight?: React.ReactNode;
}

/**
 * 企業一覧の共通表示コンポーネント。
 * 検索・フィルタ・ページネーション・グリッド表示を担う presentational コンポーネント。
 * データ取得は呼び出し側（学生用 / 管理者プレビュー用）が行う。
 */
export const CompanyListView: React.FC<CompanyListViewProps> = ({
  companies,
  loading,
  error,
  onDetailsClick,
  title = '企業一覧',
  subtitle = '気になる企業のプロフィールをチェックしましょう',
  headerRight,
}) => {
  // ─── フィルタ状態（複数選択可） ─────────────────────────
  const [keyword, setKeyword] = useState('');
  const [businessTypes, setBusinessTypes] = useState<string[]>([]);
  const [prefectures, setPrefectures] = useState<string[]>([]);
  const [jobTypes, setJobTypes] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  // ─── 実データから選択肢を動的生成 ─────────────────────
  const businessTypeOptions = useMemo(() => {
    const set = new Set<string>();
    companies.forEach((c) => c.businessType && set.add(c.businessType));
    return Array.from(set).sort();
  }, [companies]);

  const jobTypeOptions = useMemo(() => {
    const set = new Set<string>();
    companies.forEach((c) => c.jobTypes?.forEach((j) => set.add(j)));
    return Array.from(set).sort();
  }, [companies]);

  // ─── フィルタリング処理（元のソート順を維持＝スポンサー優先） ───
  // 同一カテゴリ内: OR ／ カテゴリ間: AND
  const filteredCompanies = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return companies.filter((c) => {
      if (kw && !c.companyName.toLowerCase().includes(kw)) return false;
      if (businessTypes.length > 0 && !businessTypes.includes(c.businessType ?? '')) return false;
      if (prefectures.length > 0 && !prefectures.includes(c.prefecture ?? '')) return false;
      if (jobTypes.length > 0) {
        const companyJobs = c.jobTypes ?? [];
        const hasMatch = jobTypes.some((j) => companyJobs.includes(j));
        if (!hasMatch) return false;
      }
      return true;
    });
  }, [companies, keyword, businessTypes, prefectures, jobTypes]);

  // ─── ページネーション ─────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filteredCompanies.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedCompanies = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredCompanies.slice(start, start + PAGE_SIZE);
  }, [filteredCompanies, safePage]);

  // フィルタ変更時はページ1に戻す
  useEffect(() => {
    setPage(1);
  }, [keyword, businessTypes, prefectures, jobTypes]);

  const handleClearFilters = useCallback(() => {
    setKeyword('');
    setBusinessTypes([]);
    setPrefectures([]);
    setJobTypes([]);
  }, []);

  // multi-select の値変更ハンドラ生成
  const handleMultiChange = (
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(toMultiSelectValues(e.target.value as string | string[]));
  };

  // ─── アクティブフィルタチップ ───────────────────────────
  const activeFilters: Array<{ label: string; onRemove: () => void }> = [];
  if (keyword) {
    activeFilters.push({ label: `検索: ${keyword}`, onRemove: () => setKeyword('') });
  }
  businessTypes.forEach((bt) => {
    activeFilters.push({
      label: bt,
      onRemove: () => setBusinessTypes((prev) => prev.filter((x) => x !== bt)),
    });
  });
  prefectures.forEach((p) => {
    activeFilters.push({
      label: p,
      onRemove: () => setPrefectures((prev) => prev.filter((x) => x !== p)),
    });
  });
  jobTypes.forEach((j) => {
    activeFilters.push({
      label: j,
      onRemove: () => setJobTypes((prev) => prev.filter((x) => x !== j)),
    });
  });

  const rangeStart = filteredCompanies.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filteredCompanies.length);

  // 選択中のラベル表示
  const renderMultiValue = (selected: string[]) => {
    if (selected.length === 0) return '';
    if (selected.length === 1) return selected[0];
    return `${selected[0]} ほか ${selected.length - 1} 件`;
  };

  return (
    <>
      {/* ─── ヘッダー ─── */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.35rem', sm: '1.6rem' },
              color: '#2d2d2d',
              letterSpacing: '-0.01em',
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.82rem', sm: '0.9rem' },
              color: 'text.secondary',
            }}
          >
            {subtitle}
          </Typography>
        </Box>
        {headerRight && <Box sx={{ flexShrink: 0 }}>{headerRight}</Box>}
      </Box>

      {/* ─── フィルタパネル ─── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          borderRadius: 2,
          border: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        {/* 検索ボックス */}
        <TextField
          fullWidth
          size="small"
          placeholder="企業名で検索..."
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* セレクトフィルタ（複数選択） */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems="stretch"
        >
          <TextField
            select
            size="small"
            label="業種"
            value={businessTypes}
            onChange={handleMultiChange(setBusinessTypes)}
            sx={{ flex: 1, minWidth: 0 }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => renderMultiValue(selected as string[]),
            }}
          >
            {businessTypeOptions.length === 0 && (
              <MenuItem disabled>選択肢がありません</MenuItem>
            )}
            {businessTypeOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Checkbox size="small" checked={businessTypes.includes(opt)} />
                <ListItemText primary={opt} />
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="都道府県"
            value={prefectures}
            onChange={handleMultiChange(setPrefectures)}
            sx={{ flex: 1, minWidth: 0 }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => renderMultiValue(selected as string[]),
            }}
          >
            {PREFECTURES.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Checkbox size="small" checked={prefectures.includes(opt)} />
                <ListItemText primary={opt} />
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="募集職種"
            value={jobTypes}
            onChange={handleMultiChange(setJobTypes)}
            sx={{ flex: 1, minWidth: 0 }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => renderMultiValue(selected as string[]),
            }}
          >
            {jobTypeOptions.length === 0 && (
              <MenuItem disabled>選択肢がありません</MenuItem>
            )}
            {jobTypeOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Checkbox size="small" checked={jobTypes.includes(opt)} />
                <ListItemText primary={opt} />
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {/* アクティブフィルタチップ */}
        {activeFilters.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              alignItems: 'center',
              mt: 2,
              pt: 2,
              borderTop: '1px dashed rgba(0,0,0,0.08)',
            }}
          >
            <FilterListIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            {activeFilters.map((f, idx) => (
              <Chip
                key={`${f.label}-${idx}`}
                label={f.label}
                size="small"
                onDelete={f.onRemove}
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  bgcolor: 'rgba(102,126,234,0.1)',
                  color: '#5a4a9a',
                  '& .MuiChip-deleteIcon': { fontSize: 16 },
                }}
              />
            ))}
            <Button
              size="small"
              onClick={handleClearFilters}
              sx={{
                ml: 'auto',
                fontSize: '0.75rem',
                color: 'text.secondary',
                textTransform: 'none',
              }}
            >
              すべてクリア
            </Button>
          </Box>
        )}
      </Paper>

      {/* ─── 件数表示 ─── */}
      {!loading && !error && (
        <Typography
          sx={{
            fontSize: '0.85rem',
            color: 'text.secondary',
            mb: 2,
            px: 0.5,
          }}
        >
          全 {filteredCompanies.length} 件
          {filteredCompanies.length > 0 && ` 中 ${rangeStart} - ${rangeEnd} 件を表示`}
        </Typography>
      )}

      {/* ─── エラー表示 ─── */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          企業情報の取得に失敗しました。時間をおいて再度お試しください。
        </Alert>
      )}

      {/* ─── ローディングスケルトン（初回のみ） ─── */}
      {loading && companies.length === 0 ? (
        <Box
          sx={{
            columns: { xs: 2, sm: 2, md: 3, lg: 4 },
            columnGap: { xs: 1.5, sm: 2 },
          }}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                display: 'inline-block',
                width: '100%',
                breakInside: 'avoid',
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                background: '#fff',
              }}
            >
              <Skeleton variant="rectangular" height={180} />
              <Box sx={{ p: 1.5 }}>
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="100%" />
              </Box>
            </Box>
          ))}
        </Box>
      ) : filteredCompanies.length === 0 ? (
        // 0件時のメッセージ
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            textAlign: 'center',
            borderRadius: 2,
            border: '1px dashed rgba(0,0,0,0.12)',
            background: 'transparent',
          }}
        >
          <Typography sx={{ fontSize: '0.95rem', color: 'text.secondary', mb: 1 }}>
            条件に一致する企業がありません
          </Typography>
          <Button
            size="small"
            onClick={handleClearFilters}
            sx={{ mt: 1, textTransform: 'none' }}
          >
            フィルタをクリアする
          </Button>
        </Paper>
      ) : (
        <PublicCompanyGrid
          mode="authenticated"
          onDetailsClick={onDetailsClick}
          companies={pagedCompanies}
          loading={false}
          hideHeader
          showRegisterHint={false}
        />
      )}

      {/* ─── ページネーション ─── */}
      {!loading && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={safePage}
            onChange={(_, p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            color="primary"
            shape="rounded"
            size="medium"
          />
        </Box>
      )}
    </>
  );
};
